import { createContext, useContext } from 'react';
import { CaseData } from '../../types/case';
import { PendingServerChangesInfo } from '../../types/caseApi';

/**
 * Simple case context data interface
 */
export interface SimpleCaseContextData {
    case: CaseData;
    etag?: string;
}

/**
 * Simple case context interface
 */
export interface SimpleCaseContextType {
    currentCase: SimpleCaseContextData | null;
    loading: boolean;
    error: string | null;
    hasUnsavedChanges: boolean;
    isSaving: boolean;
    lastSaved: string | null;
    formData: any;
    autoSaveEnabled: boolean;
    isReadOnly: boolean; // True when case status is 'finalized', 'archived', 'isLocked' is true, the tax year is <= 2024, the case is ProDowngrade-locked, or isCalcVersionLocked
    isCalcVersionLocked: boolean; // Non-admin on a case whose state/year is turned off (Configuration > Calculations). Inputs are read-only; downloads and the tax year picker still work, and moving to an enabled year is the one edit allowed
    isProDowngradeReadOnly: boolean; // ProDowngrade: read-only specifically because the case was last saved with Pro and the team is no longer Pro. Lets the UI say "copy this case to edit it" instead of the generic finalized/archived wording
    lastBlurTime: number | null; // Timestamp when text input last blurred (to reset debounce progress bar)
    isEditingTextInput: boolean; // True when actively editing a text/monetary input (before blur)
    calculationError: Error | null; // Current calculation error, if any
    calcsLoadError: string | null; // Set when the calcs chunk fails to load (e.g. network); user should refresh
    toggleAutoSave: (enabled: boolean) => void;
    refreshCase: () => Promise<void>;
    updateField: (field: string, value: any, isTextInput?: boolean) => void;
    updateNestedField: (path: string, value: any, isTextInput?: boolean) => void;
    // Returns a Promise that resolves once the callback's result has been recalculated and
    // committed to formState (the implementation is async — it awaits a getCalcs() import
    // before calling setFormState). A caller that needs the commit to have landed before
    // proceeding (e.g. before a subsequent forceSave()) should await this.
    updateNestedFieldCallback: (callback: (mergedCase: CaseData) => CaseData, isTextInput?: boolean) => Promise<void>;
    triggerTextInputBlur: () => void;
    forceSave: () => Promise<void>;
    initializeNewCase: (caseData: CaseData) => void;
    clearCalculationError: () => void; // Clear the calculation error
    pendingServerChanges: PendingServerChangesInfo | null; // Set when server has data the client hasn't merged
    triggerMerge: () => Promise<void>; // Explicitly merge pending server-side changes; UI must show a blocking modal during the call
    dismissPendingChanges: () => void; // "Not Yet" -- hide the import prompt; it re-surfaces on the next autosave while still pending
    isMerging: boolean; // True while triggerMerge is in flight; UI should block input
    saveConflictActive: boolean; // True when the last autosave failed with a 412 (another session saved first) and awaits user resolution
    resolveSaveConflict: (action: 'makeCopy' | 'forceSave' | 'cancel') => Promise<void>; // Resolve the active save conflict per the user's choice
    releaseCaseEditingLock: () => Promise<void>; // ConcurrentCaseEditGuard: clear the "currently editing" marker; await before navigating away from the wizard so the next page doesn't race a stale marker
}

export const SimpleCaseContext = createContext<SimpleCaseContextType | undefined>(undefined);

/**
 * Hook to use the simple case context
 */
export const useSimpleCaseContext = (): SimpleCaseContextType => {
    const context = useContext(SimpleCaseContext);
    if (context === undefined) {
        throw new Error('useSimpleCaseContext must be used within a SimpleCaseProvider');
    }
    return context;
};
